import { z } from "zod";

import { faultTypeSchema, shortCircuitCaseSchema } from "./faultContract";

// The controlled vocabularies of the earthing check (EOS-08a, Master Prompt A36), copied
// verbatim from the backend so a value the API can send is never rejected here. Sources at
// the EOS-08a commit 5: backend/app/domain/electrical/earthing/models.py (SystemKind,
// ConductorRole, ConductorMaterial, KTableId with its columns, ElectrodeKind) and
// .../results.py (EarthingStatus, EarthingWarningCode and the three severity sets, the three
// level sets, the seven origins); .../earth_fault_loop/results.py for the linked row's status
// and time origin. earthingContract.test.ts pins each list.
//
// The fault run's case and type are the fault module's enums: reused from that contract.

export const earthingStatusSchema = z.enum(["DESIGN_CHECK_PASSED", "REVIEW_REQUIRED", "NO_SOLUTION"]);

export const earthingWarningCodeSchema = z.enum([
  "PROTECTIVE_CONDUCTOR_AREA_INSUFFICIENT",
  "ELECTRODE_RESISTANCE_ABOVE_TARGET",
  "FAULT_CURRENT_NOT_ESTABLISHED",
  "FAULT_RUN_NOT_MAXIMUM_EARTH_FAULT",
  "FAULT_RUN_BUS_MISMATCH",
  "DISCONNECTION_TIME_NOT_ESTABLISHED",
  "DISCONNECTION_TIME_ABOVE_ADIABATIC_LIMIT",
  "DISCONNECTION_ROW_NOT_ASSURED",
  "DISCONNECTION_ROW_NOT_COMPARED",
  "DISCONNECTION_RUN_BUS_MISMATCH",
  "K_FACTOR_NOT_ESTABLISHED",
  "K_TABLE_CASE_NOT_COVERED",
  "PROTECTIVE_CONDUCTOR_AREA_NOT_ESTABLISHED",
  "CONDUCTOR_MATERIAL_NOT_ESTABLISHED",
  "SOIL_RESISTIVITY_NOT_ESTABLISHED",
  "SOIL_TEST_RECORD_NOT_DECLARED",
  "TARGET_RESISTANCE_NOT_ESTABLISHED",
  "PARALLEL_FACTOR_NOT_ESTABLISHED",
  "FAULT_CURRENT_SOURCE_NOT_DECLARED",
  "LET_THROUGH_ENERGY_SOURCE_NOT_DECLARED",
  "DISCONNECTION_TIME_SOURCE_NOT_DECLARED",
  "K_FACTOR_SOURCE_NOT_DECLARED",
  "PROTECTIVE_CONDUCTOR_AREA_SOURCE_NOT_DECLARED",
  "SEASONAL_FACTOR_SOURCE_NOT_DECLARED",
  "PARALLEL_FACTOR_SOURCE_NOT_DECLARED",
  "TARGET_RESISTANCE_SOURCE_NOT_DECLARED",
  "ELECTRODE_DIMENSION_SOURCE_NOT_DECLARED",
  "SYSTEM_VOLTAGE_SOURCE_NOT_DECLARED",
  "DECLARED_VALUE_GOVERNS",
  "FAULT_CURRENT_DECLARED",
  "FAULT_CURRENT_FROM_DISCONNECTION_ROW",
  "LET_THROUGH_ENERGY_DECLARED",
  "DISCONNECTION_TIME_FROM_LINKED_ROW",
  "T_IS_MAXIMUM_PERMITTED_TIME",
  "K_FROM_REGISTER_TABLE",
  "K_DECLARED_NOT_VERIFIED",
  "STANDARD_SIZE_NOT_SELECTED",
  "PARALLEL_SPACING_NOT_CHECKED",
  "SEASONAL_FACTOR_APPLIED",
  "SEASONAL_FACTOR_NOT_DECLARED",
  "ARRANGEMENT_IDEAL_PARALLEL",
  "UNIFORM_SOIL_ASSUMED",
  "STEP_TOUCH_VOLTAGE_NOT_CHECKED",
  "LIGHTNING_EARTHING_NOT_CHECKED",
  "EARTH_LOOP_BONDING_CONTINUITY_NOT_CHECKED",
  "CORROSION_ALLOWANCE_NOT_APPLIED",
  "ELECTRODE_CURRENT_LOADING_NOT_CHECKED",
  "MINIMUM_DIMENSIONS_NOT_CHECKED",
  "SELECTION_TABLE_NOT_APPLIED",
  "BONDING_CONDUCTORS_NOT_SIZED",
  "JOINT_TEMPERATURE_NOT_CHECKED",
]);

export const systemKindSchema = z.enum(["TN_S", "TN_C_S", "TT", "IT"]);
export const conductorRoleSchema = z.enum(["CIRCUIT_PROTECTIVE", "MAIN_PROTECTIVE", "EARTHING_CONDUCTOR"]);
export const conductorMaterialSchema = z.enum(["COPPER", "ALUMINIUM", "STEEL"]);
export const kTableIdSchema = z.enum([
  "IS3043_11A",
  "IS3043_11B",
  "IS3043_11C",
  "IS3043_11D",
  "CPWD_F6",
  "CPWD_F7",
]);
export const electrodeKindSchema = z.enum(["ROD_OR_PIPE", "PLATE", "STRIP", "ROUND_CONDUCTOR"]);

/** The columns each held table prints (the data files' column ids); a case names one of them. */
export const K_TABLE_COLUMNS = {
  IS3043_11A: ["BARE"],
  IS3043_11B: ["PVC", "BUTYL_RUBBER", "XLPE_EPR"],
  IS3043_11C: ["PVC", "BUTYL_RUBBER", "XLPE_EPR"],
  IS3043_11D: ["FINAL_150", "FINAL_200"],
  CPWD_F6: ["VISIBLE_RESTRICTED", "NORMAL", "FIRE_RISK"],
  CPWD_F7: ["THERMOPLASTIC", "PVC", "POLYETHYLENE_CSP"],
} as const satisfies Record<z.infer<typeof kTableIdSchema>, readonly string[]>;

/** The dimension fields each electrode kind's printed formula takes. */
export const ELECTRODE_DIMENSIONS = {
  ROD_OR_PIPE: ["rod_length_m", "rod_diameter_mm"],
  PLATE: ["plate_length_m", "plate_width_m"],
  STRIP: ["strip_length_m", "strip_width_mm"],
  ROUND_CONDUCTOR: ["conductor_length_m", "conductor_diameter_mm"],
} as const satisfies Record<z.infer<typeof electrodeKindSchema>, readonly string[]>;

export const faultCurrentOriginSchema = z.enum([
  "LINKED_FAULT_RUN",
  "LINKED_DISCONNECTION_ROW",
  "DECLARED",
  "NOT_ESTABLISHED",
]);
export const disconnectionTimeSourceSchema = z.enum([
  "LINKED_DISCONNECTION_ROW",
  "DECLARED",
  "NOT_ESTABLISHED",
]);
export const energyBasisSchema = z.enum([
  "COMPUTED_FROM_CURRENT_AND_TIME",
  "DECLARED",
  "NOT_ESTABLISHED",
]);
export const kOriginSchema = z.enum(["REGISTER_VERIFIED", "DECLARED", "NOT_ESTABLISHED"]);
export const areaOriginSchema = z.enum(["DECLARED", "LINKED_DISCONNECTION_ROW", "NOT_ESTABLISHED"]);
export const seasonalFactorOriginSchema = z.enum(["DECLARED", "NOT_DECLARED", "NOT_APPLICABLE"]);
export const targetOriginSchema = z.enum(["DECLARED", "NOT_ESTABLISHED"]);

// The linked EOS-05d row's own vocabularies (earth_fault_loop/results.py).
export const earthFaultLoopStatusSchema = z.enum([
  "VALID",
  "WARNING",
  "REVIEW_REQUIRED",
  "NO_SOLUTION",
]);
export const disconnectionTimeOriginSchema = z.enum(["DECLARED", "REGISTER_VERIFIED", "NOT_ESTABLISHED"]);

export { faultTypeSchema, shortCircuitCaseSchema };

/** A computed failure only (D11): NO_SOLUTION. */
export const NO_SOLUTION_WARNING_CODES = [
  "PROTECTIVE_CONDUCTOR_AREA_INSUFFICIENT",
  "ELECTRODE_RESISTANCE_ABOVE_TARGET",
] as const;

/** A condition that could not be evaluated, a link to look at, or a source still owed. */
export const REVIEW_REQUIRED_WARNING_CODES = [
  "FAULT_CURRENT_NOT_ESTABLISHED",
  "FAULT_RUN_NOT_MAXIMUM_EARTH_FAULT",
  "FAULT_RUN_BUS_MISMATCH",
  "DISCONNECTION_TIME_NOT_ESTABLISHED",
  "DISCONNECTION_TIME_ABOVE_ADIABATIC_LIMIT",
  "DISCONNECTION_ROW_NOT_ASSURED",
  "DISCONNECTION_ROW_NOT_COMPARED",
  "DISCONNECTION_RUN_BUS_MISMATCH",
  "K_FACTOR_NOT_ESTABLISHED",
  "K_TABLE_CASE_NOT_COVERED",
  "PROTECTIVE_CONDUCTOR_AREA_NOT_ESTABLISHED",
  "CONDUCTOR_MATERIAL_NOT_ESTABLISHED",
  "SOIL_RESISTIVITY_NOT_ESTABLISHED",
  "SOIL_TEST_RECORD_NOT_DECLARED",
  "TARGET_RESISTANCE_NOT_ESTABLISHED",
  "PARALLEL_FACTOR_NOT_ESTABLISHED",
  "FAULT_CURRENT_SOURCE_NOT_DECLARED",
  "LET_THROUGH_ENERGY_SOURCE_NOT_DECLARED",
  "DISCONNECTION_TIME_SOURCE_NOT_DECLARED",
  "K_FACTOR_SOURCE_NOT_DECLARED",
  "PROTECTIVE_CONDUCTOR_AREA_SOURCE_NOT_DECLARED",
  "SEASONAL_FACTOR_SOURCE_NOT_DECLARED",
  "PARALLEL_FACTOR_SOURCE_NOT_DECLARED",
  "TARGET_RESISTANCE_SOURCE_NOT_DECLARED",
  "ELECTRODE_DIMENSION_SOURCE_NOT_DECLARED",
  "SYSTEM_VOLTAGE_SOURCE_NOT_DECLARED",
] as const;

/** What the sheet states; never moves the status. */
export const INFORMATION_WARNING_CODES = [
  "DECLARED_VALUE_GOVERNS",
  "FAULT_CURRENT_DECLARED",
  "FAULT_CURRENT_FROM_DISCONNECTION_ROW",
  "LET_THROUGH_ENERGY_DECLARED",
  "DISCONNECTION_TIME_FROM_LINKED_ROW",
  "T_IS_MAXIMUM_PERMITTED_TIME",
  "K_FROM_REGISTER_TABLE",
  "K_DECLARED_NOT_VERIFIED",
  "STANDARD_SIZE_NOT_SELECTED",
  "PARALLEL_SPACING_NOT_CHECKED",
  "SEASONAL_FACTOR_APPLIED",
  "SEASONAL_FACTOR_NOT_DECLARED",
  "ARRANGEMENT_IDEAL_PARALLEL",
  "UNIFORM_SOIL_ASSUMED",
  "STEP_TOUCH_VOLTAGE_NOT_CHECKED",
  "LIGHTNING_EARTHING_NOT_CHECKED",
  "EARTH_LOOP_BONDING_CONTINUITY_NOT_CHECKED",
  "CORROSION_ALLOWANCE_NOT_APPLIED",
  "ELECTRODE_CURRENT_LOADING_NOT_CHECKED",
  "MINIMUM_DIMENSIONS_NOT_CHECKED",
  "SELECTION_TABLE_NOT_APPLIED",
  "BONDING_CONDUCTORS_NOT_SIZED",
  "JOINT_TEMPERATURE_NOT_CHECKED",
] as const;

/** The codes a conductor row may carry. */
export const ROW_WARNING_CODES = [
  "PROTECTIVE_CONDUCTOR_AREA_INSUFFICIENT",
  "FAULT_CURRENT_NOT_ESTABLISHED",
  "FAULT_RUN_NOT_MAXIMUM_EARTH_FAULT",
  "FAULT_RUN_BUS_MISMATCH",
  "DISCONNECTION_TIME_NOT_ESTABLISHED",
  "DISCONNECTION_TIME_ABOVE_ADIABATIC_LIMIT",
  "DISCONNECTION_ROW_NOT_ASSURED",
  "DISCONNECTION_ROW_NOT_COMPARED",
  "DISCONNECTION_RUN_BUS_MISMATCH",
  "K_FACTOR_NOT_ESTABLISHED",
  "K_TABLE_CASE_NOT_COVERED",
  "PROTECTIVE_CONDUCTOR_AREA_NOT_ESTABLISHED",
  "CONDUCTOR_MATERIAL_NOT_ESTABLISHED",
  "FAULT_CURRENT_SOURCE_NOT_DECLARED",
  "LET_THROUGH_ENERGY_SOURCE_NOT_DECLARED",
  "DISCONNECTION_TIME_SOURCE_NOT_DECLARED",
  "K_FACTOR_SOURCE_NOT_DECLARED",
  "PROTECTIVE_CONDUCTOR_AREA_SOURCE_NOT_DECLARED",
  "DECLARED_VALUE_GOVERNS",
  "FAULT_CURRENT_DECLARED",
  "FAULT_CURRENT_FROM_DISCONNECTION_ROW",
  "LET_THROUGH_ENERGY_DECLARED",
  "DISCONNECTION_TIME_FROM_LINKED_ROW",
  "T_IS_MAXIMUM_PERMITTED_TIME",
  "K_FROM_REGISTER_TABLE",
  "K_DECLARED_NOT_VERIFIED",
  "STANDARD_SIZE_NOT_SELECTED",
] as const;

/** The codes an electrode row may carry. */
export const ELECTRODE_WARNING_CODES = [
  "PARALLEL_FACTOR_NOT_ESTABLISHED",
  "PARALLEL_FACTOR_SOURCE_NOT_DECLARED",
  "ELECTRODE_DIMENSION_SOURCE_NOT_DECLARED",
  "PARALLEL_SPACING_NOT_CHECKED",
] as const;

/** The nine items every study states it did not check (D12; the F-1 lesson of IR-005). */
export const ALWAYS_STUDY_INFORMATION_CODES = [
  "STEP_TOUCH_VOLTAGE_NOT_CHECKED",
  "LIGHTNING_EARTHING_NOT_CHECKED",
  "EARTH_LOOP_BONDING_CONTINUITY_NOT_CHECKED",
  "CORROSION_ALLOWANCE_NOT_APPLIED",
  "ELECTRODE_CURRENT_LOADING_NOT_CHECKED",
  "MINIMUM_DIMENSIONS_NOT_CHECKED",
  "SELECTION_TABLE_NOT_APPLIED",
  "BONDING_CONDUCTORS_NOT_SIZED",
  "JOINT_TEMPERATURE_NOT_CHECKED",
] as const;

export type EarthingStatus = z.infer<typeof earthingStatusSchema>;
export type EarthingWarningCode = z.infer<typeof earthingWarningCodeSchema>;
export type SystemKind = z.infer<typeof systemKindSchema>;
export type ConductorRole = z.infer<typeof conductorRoleSchema>;
export type ConductorMaterial = z.infer<typeof conductorMaterialSchema>;
export type KTableId = z.infer<typeof kTableIdSchema>;
export type ElectrodeKind = z.infer<typeof electrodeKindSchema>;
export type FaultCurrentOrigin = z.infer<typeof faultCurrentOriginSchema>;
export type DisconnectionTimeSource = z.infer<typeof disconnectionTimeSourceSchema>;
export type EnergyBasis = z.infer<typeof energyBasisSchema>;
export type KOrigin = z.infer<typeof kOriginSchema>;
export type AreaOrigin = z.infer<typeof areaOriginSchema>;
export type SeasonalFactorOrigin = z.infer<typeof seasonalFactorOriginSchema>;
export type TargetOrigin = z.infer<typeof targetOriginSchema>;
export type EarthFaultLoopStatus = z.infer<typeof earthFaultLoopStatusSchema>;
export type DisconnectionTimeOrigin = z.infer<typeof disconnectionTimeOriginSchema>;
